// MACHINERY V2 (Stroje) — compact catalog pagination (M2.8).
//
// Spec: data package §11 — `query.page`, `query.pageSize` ([20,50,100]),
// "showingResults from browseTotalCount + local page math". Artboards 0900 /
// 0901 draw the strip: ‹ 1 2 3 … 302 303 ›.
//
// LOCAL PAGE MATH, DELIBERATELY. The search result carries `totalPages`, and
// the slice even stores it as `browseTotalPages` — but §11 records it as DEAD
// STATE ("NO component reads it") and specifies the count from
// `browseTotalCount` + the LOCAL page size. Keeping the math local means the
// page numbers stay consistent with the page size the user just picked, even
// in the tick before the next response lands. Same choice v1 made.
//
// THE WINDOW: first, last, and a run around the current page, with a single
// ellipsis on each side. `…` is a static separator, never a button — a
// clickable ellipsis implies a destination the user cannot predict.
//
// ============================================================
//  `touch` — THE ONLY BELOW-1280 CHANGE THIS FILE CARRIES (M2.16c review, F5)
// ============================================================
// This strip renders OUTSIDE CatalogBrowseV2's desktop branch, i.e. at every
// width, and its controls are 28x28 with a 4px gap — a pointer size. On a 390px
// phone paging 124 models that is nine adjacent sub-target buttons, and the
// two-axis ">= 44px BOTH axes" law applies to them like anything else.
//
// THE PROP DEFAULTS TO THE DESKTOP LITERALS, so >= 1280 emits exactly the
// numbers it always did — this component is otherwise zero-diff in this
// sub-phase and must stay that way.
//
// >>> AND THE NAV WRAPS WHEN IT IS TOUCH-SIZED. <<< Nine 44px buttons plus
// their gaps are ~440px, wider than a 390px phone's content box, so without
// `flexWrap` the row would introduce the horizontal scroll the mobile pass
// exists to remove. Wrapping is the honest answer at that width; the desktop
// row is `nowrap` as before because it has always fitted.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import TabSelect from "../profile/tabs/TabSelect";
import { CATALOG_PAGE_SIZES } from "./catalogQuery";

export interface CatalogPaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  /** Below 1280 — see the header. Defaults to the desktop sizing. */
  touch?: boolean;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}

type Slot = number | "gap";

/** first · … · [current-1, current, current+1] · … · last */
const buildSlots = (page: number, totalPages: number): Slot[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const slots: Slot[] = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(totalPages - 1, page + 1);

  if (from > 2) slots.push("gap");
  for (let p = from; p <= to; p++) slots.push(p);
  if (to < totalPages - 1) slots.push("gap");
  slots.push(totalPages);
  return slots;
};

const navBtn: React.CSSProperties = {
  minWidth: 28,
  height: 28,
  padding: "0 7px",
  borderRadius: 6,
  border: "1px solid var(--border)",
  background: "var(--card)",
  color: "var(--ink-2)",
  cursor: "pointer",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  display: "inline-grid",
  placeItems: "center",
};

/** The touch-sized overlay: >= 44px on BOTH axes, one step of type so a 44px
  * box does not read as an 11px number floating in it. Spread AFTER `navBtn`,
  * never merged into it, so the desktop object is untouched. */
const navBtnTouch: React.CSSProperties = {
  minWidth: 44,
  height: 44,
  padding: "0 10px",
  borderRadius: 8,
  fontSize: 12.5,
};

const CatalogPagination: React.FC<CatalogPaginationProps> = ({
  page,
  pageSize,
  totalCount,
  touch,
  onPage,
  onPageSize,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const lang = getCurrentLanguage();
  // ONE object, built once per render — every slot below spreads it, so a
  // number button and the two arrows can never disagree about their size.
  const btn: React.CSSProperties = touch ? { ...navBtn, ...navBtnTouch } : navBtn;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        padding: "14px 0 4px",
        minWidth: 0,
      }}
    >
      {/* The "showing N-M of T" line is NOT here: artboard 0900 puts it ABOVE
          the card grid, right under the category tabs, so `CatalogShowingLine`
          renders it there and this strip carries only the controls. */}
      <span style={{ flex: 1 }} />

      {/* The v2 select primitive, NOT a native <select> (§3 pins the primitive
          library to Radix). `anyLabel` is omitted — there is no "any" page
          size; see the prop's own note in TabSelect.tsx. */}
      <TabSelect
        label={translate("myMachinery:rowsPerPage")}
        value={String(pageSize)}
        options={CATALOG_PAGE_SIZES.map((size) => ({ id: String(size), name: String(size) }))}
        onChange={(value) => {
          if (value) onPageSize(Number(value));
        }}
      />

      <nav
        aria-label={translate("myMachinery:v2.catalog.pagination")}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: touch ? 6 : 4,
          // See the header: 44px slots do not fit a phone on one line.
          flexWrap: touch ? "wrap" : "nowrap",
          minWidth: 0,
        }}
      >
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label={translate("myMachinery:v2.catalog.previousPage")}
          style={{ ...btn, opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? "default" : "pointer" }}
        >
          ‹
        </button>

        {buildSlots(page, totalPages).map((slot, index) =>
          slot === "gap" ? (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={`gap-${index}`}
              aria-hidden
              style={{
                width: 20,
                // Matches the slot height it sits between, so a wrapped touch
                // row keeps one baseline. It is a separator, never a target.
                lineHeight: touch ? "44px" : undefined,
                textAlign: "center",
                color: "var(--faint)",
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              …
            </span>
          ) : (
            <button
              key={slot}
              type="button"
              onClick={() => onPage(slot)}
              aria-current={slot === page ? "page" : undefined}
              style={{
                ...btn,
                background: slot === page ? "var(--mod-accent)" : "var(--card)",
                borderColor: slot === page ? "var(--mod-accent)" : "var(--border)",
                color: slot === page ? "var(--mod-accent-fg)" : "var(--ink-2)",
                fontWeight: slot === page ? 700 : 400,
              }}
            >
              {slot.toLocaleString(lang)}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          aria-label={translate("myMachinery:v2.catalog.nextPage")}
          style={{
            ...btn,
            opacity: page >= totalPages ? 0.4 : 1,
            cursor: page >= totalPages ? "default" : "pointer",
          }}
        >
          ›
        </button>
      </nav>
    </div>
  );
};

export default CatalogPagination;
